import React, { useMemo, useContext } from 'react';
import useTimesheetFields from '@ubeya/shared/hooks/account/useTimesheetFields';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { mappedArray } from '@ubeya/shared/utils/array';
import { getTimesheetFieldsCompletedCount } from '@ubeya/shared/utils/timesheetFields';
import { isArray } from 'lodash';
import { TimesheetTaskFieldRenderer } from '../components';
import ConfigContext from '../contexts/ConfigContext';
import PrivilegesContext from '../contexts/PrivilegesContext';
import privileges from '../privileges';
import { FIELDS_MAP } from './useFormFields';

// Shift task field types that support inline editing in the timesheet table.
const EDITABLE_FIELD_TYPES = [
  FieldTypes.FIELD_TYPE_TEXT,
  FieldTypes.FIELD_TYPE_NUMBER,
  FieldTypes.FIELD_TYPE_DATE,
  FieldTypes.FIELD_TYPE_OPTIONS,
  FieldTypes.FIELD_TYPE_BOOLEAN
];

/**
 * Hook to generate timesheet fields columns for tables
 * @param {Object} options - Configuration options
 * @param {boolean} options.showTimesheetFields - Whether to show timesheet fields columns
 * @param {Function} options.updateTimesheetTaskField - Mutation to persist an inline field edit
 * @param {string} options.amplitudeModuleName - Module name reported on the edit amplitude event
 * @returns {Array} - Array of column definitions for timesheet fields
 */
const useTimesheetFieldsColumns = ({
  showTimesheetFields = false,
  updateTimesheetTaskField,
  amplitudeModuleName = 'Timesheet module'
} = {}) => {
  const { t, dateFormat = 'DD/MM/YYYY' } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const { fields: timesheetFields } = useTimesheetFields();

  // Editing task field entries reuses the existing View Timesheets privilege.
  const canEditTimesheetFields = !!updateTimesheetTaskField && !!myPrivileges?.[privileges.TIMESHEET];

  const timesheetFieldsColumns = useMemo(() => {
    if (!showTimesheetFields || !timesheetFields.length) {
      return [];
    }

    const columns = [];

    // Add the completed fields status column
    columns.push({
      Header: t('sequencesResultsCompletedFieldsStatus'),
      dataKey: 'completedFields',
      Aggregate: ({ rowData }) => {
        const completedFields = (rowData.subRows || []).reduce((total, subRow) => {
          const result = getTimesheetFieldsCompletedCount(subRow);
          return total + result;
        }, 0);

        return <span>{completedFields}</span>;
      },
      formatter: (_, row) => {
        const completedFields = getTimesheetFieldsCompletedCount(row);
        return completedFields;
      },
      width: 140,
      category: t('timesheetFields'),
      visible: true,
      cellWrapperProps: { style: { justifyContent: 'center' } }
    });

    // Add individual field columns
    columns.push(
      ...timesheetFields.map((field) => {
        const fieldHeader = field.isDeleted ? `${field.name} (${t('deleted')})` : field.name;
        const fieldDataKey = `mappedTimesheetFieldsValues.${field.id}`;

        const formatter = (value) => {
          const mapOptions =
            field.options &&
            mappedArray(
              field.options,
              ({ id: optionId }) => optionId,
              ({ title }) => title
            );

          const formattedValue = FIELDS_MAP[field.fieldTypeId]?.viewFormatter?.(value, {
            format: dateFormat,
            mapOptions
          });

          // If the formatted value is an array, join the values with a comma
          // This can only happen for dropdown fields
          if (formattedValue && isArray(formattedValue)) {
            return formattedValue.join(',');
          }

          return formattedValue || value;
        };

        const generalColumnProps = {
          Header: fieldHeader,
          dataKey: fieldDataKey,
          category: t('timesheetFields'),
          visible: false
        };

        if (EDITABLE_FIELD_TYPES.includes(field.fieldTypeId)) {
          return {
            Cell: TimesheetTaskFieldRenderer,
            cellRendererProps: { hidden: false },
            columnData: {
              field,
              onEdit: updateTimesheetTaskField,
              canEdit: canEditTimesheetFields,
              dateFormat,
              formatValue: formatter,
              amplitudeModuleName
            },
            // Give date fields a bit more room so the date + picker aren't clipped.
            ...(field.fieldTypeId === FieldTypes.FIELD_TYPE_DATE ? { minWidth: 140 } : {}),
            ...generalColumnProps
          };
        }

        // Fallback for any non-editable field type: the grid renders the value via `formatter`.
        // (Document/signature types can't be timesheet fields, so they never reach here.)
        return {
          formatter,
          cellRendererProps: {
            tooltipBodyMaxWidth: 700
          },
          ...generalColumnProps
        };
      })
    );

    return columns;
  }, [
    showTimesheetFields,
    timesheetFields,
    t,
    dateFormat,
    canEditTimesheetFields,
    updateTimesheetTaskField,
    amplitudeModuleName
  ]);

  return timesheetFieldsColumns;
};

export default useTimesheetFieldsColumns;
